import {
  useLayoutEffect,
  useRef,
  useState,
  type ClipboardEvent,
  type DragEvent,
  type KeyboardEvent,
} from "react";
import { api } from "@/lib/api";
import { MarkdownView } from "./MarkdownView";
import { toast } from "./Toast";
import {
  applyInlineFormat,
  toggleLinePrefix,
  insertLink,
  insertText,
  insertCodeBlock,
  htmlToMarkdown,
  type EditResult,
} from "@/lib/markdownEditing";

/**
 * RichTextEditor — the one markdown composer reused across task descriptions,
 * comments, and docs. It is a textarea with a formatting toolbar, a slash
 * menu, paste-from-HTML cleanup, and image paste/drop → R2. Output is a
 * markdown string (rendered XSS-safe by MarkdownView / renderMarkdown), so the
 * storage format is unchanged from the old plain-textarea fields.
 *
 * Image uploads need a project context (`upload` prop). Pasted/dropped images
 * go to R2 via the existing attachment uploader and embed as same-origin
 * `/api/attachments/<id>/raw` URLs — the only image form the sanitiser allows.
 */
export type UploadContext = { taskId: string } | { projectId: string };

type Sel = { value: string; start: number; end: number };
const TOOLBAR: {
  label: string;
  cls?: string;
  title: string;
  apply: (sel: Sel) => EditResult;
}[] = [
  {
    label: "B",
    cls: "rte-b",
    title: "Bold (⌘B)",
    apply: (s) => applyInlineFormat(s, "**"),
  },
  {
    label: "I",
    cls: "rte-i",
    title: "Italic (⌘I)",
    apply: (s) => applyInlineFormat(s, "*"),
  },
  { label: "H1", title: "Heading 1", apply: (s) => toggleLinePrefix(s, "# ") },
  { label: "H2", title: "Heading 2", apply: (s) => toggleLinePrefix(s, "## ") },
  {
    label: "•",
    title: "Bulleted list",
    apply: (s) => toggleLinePrefix(s, "- "),
  },
  {
    label: "1.",
    title: "Numbered list",
    apply: (s) => toggleLinePrefix(s, "1. ", true),
  },
  { label: "❝", title: "Quote", apply: (s) => toggleLinePrefix(s, "> ") },
  {
    label: "</>",
    title: "Inline code",
    apply: (s) => applyInlineFormat(s, "`"),
  },
  { label: "{ }", title: "Code block", apply: (s) => insertCodeBlock(s) },
];

const SLASH_ITEMS: {
  label: string;
  hint: string;
  apply: (sel: { value: string; start: number; end: number }) => EditResult;
}[] = [
  { label: "Heading 1", hint: "# ", apply: (s) => toggleLinePrefix(s, "# ") },
  { label: "Heading 2", hint: "## ", apply: (s) => toggleLinePrefix(s, "## ") },
  {
    label: "Heading 3",
    hint: "### ",
    apply: (s) => toggleLinePrefix(s, "### "),
  },
  {
    label: "Bulleted list",
    hint: "- ",
    apply: (s) => toggleLinePrefix(s, "- "),
  },
  {
    label: "Numbered list",
    hint: "1.",
    apply: (s) => toggleLinePrefix(s, "1. ", true),
  },
  { label: "Quote", hint: "> ", apply: (s) => toggleLinePrefix(s, "> ") },
  { label: "Code block", hint: "```", apply: (s) => insertCodeBlock(s) },
  { label: "Divider", hint: "---", apply: (s) => insertText(s, "\n\n---\n\n") },
];

export function RichTextEditor({
  value,
  onChange,
  placeholder,
  upload,
  minRows = 4,
  id,
  ariaLabel,
  autoFocus,
}: {
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  upload?: UploadContext;
  minRows?: number;
  id?: string;
  ariaLabel?: string;
  autoFocus?: boolean;
}) {
  const ref = useRef<HTMLTextAreaElement>(null);
  const pendingSel = useRef<[number, number] | null>(null);
  const [tab, setTab] = useState<"write" | "preview">("write");
  const [slashOpen, setSlashOpen] = useState(false);
  const [uploading, setUploading] = useState(false);

  // Re-apply a programmatic selection after the controlled value updates.
  useLayoutEffect(() => {
    if (pendingSel.current && ref.current) {
      const [s, e] = pendingSel.current;
      ref.current.focus();
      ref.current.setSelectionRange(s, e);
      pendingSel.current = null;
    }
  });

  const sel = () => {
    const el = ref.current!;
    return { value, start: el.selectionStart, end: el.selectionEnd };
  };

  const applyResult = (r: EditResult) => {
    pendingSel.current = [r.start, r.end];
    onChange(r.value);
  };

  const runFormat = (apply: (s: ReturnType<typeof sel>) => EditResult) => {
    if (!ref.current) return;
    applyResult(apply(sel()));
  };

  const doLink = () => {
    const url = window.prompt("Link URL");
    if (url) runFormat((s) => insertLink(s, url));
  };

  const uploadImage = async (file: File) => {
    if (!upload) {
      toast.error("Save first, then paste images.");
      return;
    }
    if (!file.type.startsWith("image/")) return;
    setUploading(true);
    try {
      const { id: attId } = await api.uploadAttachment(upload, file);
      const md = `\n![${file.name}](/api/attachments/${attId}/raw)\n`;
      const el = ref.current;
      const s = el
        ? { value, start: el.selectionStart, end: el.selectionEnd }
        : { value, start: value.length, end: value.length };
      applyResult(insertText(s, md));
    } catch {
      toast.error("Image upload failed.");
    } finally {
      setUploading(false);
    }
  };

  const onPaste = (e: ClipboardEvent<HTMLTextAreaElement>) => {
    const imageFile = Array.from(e.clipboardData.files).find((f) =>
      f.type.startsWith("image/"),
    );
    if (imageFile) {
      e.preventDefault();
      void uploadImage(imageFile);
      return;
    }
    const html = e.clipboardData.getData("text/html");
    if (html) {
      e.preventDefault();
      const cleaned = htmlToMarkdown(html);
      applyResult(insertText(sel(), cleaned));
    }
  };

  const onDrop = (e: DragEvent<HTMLTextAreaElement>) => {
    const imageFile = Array.from(e.dataTransfer.files).find((f) =>
      f.type.startsWith("image/"),
    );
    if (imageFile) {
      e.preventDefault();
      void uploadImage(imageFile);
    }
  };

  const onKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "b") {
      e.preventDefault();
      runFormat((s) => applyInlineFormat(s, "**"));
    } else if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "i") {
      e.preventDefault();
      runFormat((s) => applyInlineFormat(s, "*"));
    } else if (e.key === "Escape" && slashOpen) {
      setSlashOpen(false);
    }
  };

  const handleChange = (next: string) => {
    onChange(next);
    // Open the slash menu when the user types "/" at the start of a line.
    const el = ref.current;
    if (el) {
      const caret = el.selectionStart;
      const justTyped = next.length === value.length + 1;
      const ch = next[caret - 1];
      const atLineStart = caret === 1 || next[caret - 2] === "\n";
      if (justTyped && ch === "/" && atLineStart) setSlashOpen(true);
      else if (slashOpen && ch !== "/") setSlashOpen(false);
    }
  };

  const pickSlash = (item: (typeof SLASH_ITEMS)[number]) => {
    setSlashOpen(false);
    const el = ref.current;
    if (!el) return;
    // Remove the trailing "/" that opened the menu, then apply the block.
    const caret = el.selectionStart;
    const withoutSlash = value.slice(0, caret - 1) + value.slice(caret);
    const s = { value: withoutSlash, start: caret - 1, end: caret - 1 };
    applyResult(item.apply(s));
  };

  return (
    <div className="rte">
      <div className="rte-bar">
        <div className="rte-tools" role="toolbar" aria-label="Formatting">
          {TOOLBAR.map((t) => (
            <button
              key={t.title}
              type="button"
              className={`rte-tool${t.cls ? ` ${t.cls}` : ""}`}
              title={t.title}
              aria-label={t.title}
              tabIndex={-1}
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => runFormat(t.apply)}
            >
              {t.label}
            </button>
          ))}
          <button
            type="button"
            className="rte-tool"
            title="Link"
            aria-label="Link"
            tabIndex={-1}
            onMouseDown={(e) => e.preventDefault()}
            onClick={doLink}
          >
            🔗
          </button>
        </div>
        <div className="rte-tabs">
          <button
            type="button"
            className={`rte-tab${tab === "write" ? " on" : ""}`}
            onClick={() => setTab("write")}
          >
            Write
          </button>
          <button
            type="button"
            className={`rte-tab${tab === "preview" ? " on" : ""}`}
            onClick={() => setTab("preview")}
          >
            Preview
          </button>
        </div>
      </div>

      {tab === "write" ? (
        <div className="rte-write">
          <textarea
            ref={ref}
            id={id}
            aria-label={ariaLabel}
            className="input rte-area"
            rows={minRows}
            placeholder={placeholder}
            value={value}
            autoFocus={autoFocus}
            onChange={(e) => handleChange(e.target.value)}
            onKeyDown={onKeyDown}
            onPaste={onPaste}
            onDrop={onDrop}
          />
          {slashOpen && (
            <div className="rte-slash" role="menu" aria-label="Insert block">
              {SLASH_ITEMS.map((it) => (
                <button
                  key={it.label}
                  type="button"
                  role="menuitem"
                  className="rte-slash-item"
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => pickSlash(it)}
                >
                  <span>{it.label}</span>
                  <span className="t-mono t-muted">{it.hint}</span>
                </button>
              ))}
            </div>
          )}
          {uploading && (
            <div className="rte-uploading t-sm t-muted">Uploading image…</div>
          )}
        </div>
      ) : (
        <div className="rte-preview">
          {value.trim() ? (
            <MarkdownView source={value} />
          ) : (
            <p className="t-muted t-sm">Nothing to preview yet.</p>
          )}
        </div>
      )}
    </div>
  );
}
